import { useState } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaLocationDot,
  FaWhatsapp,
  FaArrowRight,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa6";
import Reveal from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-green-primary">
      {label}
    </span>
    <input
      {...props}
      className="w-full rounded-2xl border border-gold-soft bg-gold-light px-5 py-4 text-sm text-green-primary outline-none transition-all placeholder:text-green-primary/60 focus:border-gold-primary focus:ring-2 focus:ring-gold-primary/30"
    />
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    contactInfo: "",
    service: "Executive Search",
    message: "",
  });

  const submit = (e) => {
    e.preventDefault();
    const text = `Hi PLACE8 Recruitment Consultancy, I'm ${
      form.name || "a prospective client"
    } (${form.contactInfo || "No phone/email provided"}). Interested in: ${
      form.service
    }. ${form.message}`.trim();
    window.open(
      `https://wa.me/918657696796?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-gold-light py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        <Reveal className="mb-14 max-w-3xl">
          <p className="mb-4 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-gold-primary">
            <span className="h-px w-8 bg-gold-primary" /> Contact PLACE8
          </p>
          <h2 className="font-display text-4xl font-extralight leading-[1.05] tracking-tightest text-green-primary md:text-6xl">
            Let's discuss your <span className="italic text-gradient-gold">hiring requirements.</span>
          </h2>
          <p className="mt-4 text-base text-green-primary md:text-lg font-medium">
            Connect with our recruitment strategy team today.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Details column */}
          <Reveal dir="left" className="flex flex-col gap-5 lg:col-span-4">
            {/* Phone */}
            <div className="rounded-3xl border border-gold-soft bg-white p-6 shadow-soft">
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-green-primary text-gold-primary">
                  <FaPhone />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-primary">
                  Phone Numbers
                </span>
              </div>
              <div className="space-y-1">
                {CONTACT.phones.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    className="block text-[15px] font-bold text-green-primary transition-colors hover:text-gold-primary"
                  >
                    {p}
                  </a>
                ))}
              </div>
            </div>

            {/* Email */}
            <div className="rounded-3xl border border-gold-soft bg-white p-6 shadow-soft">
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-green-primary text-gold-primary">
                  <FaEnvelope />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-primary">
                  Email Addresses
                </span>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] font-extrabold uppercase tracking-widest text-gold-primary/90 mb-1">
                    For Jobseekers
                  </span>
                  {CONTACT.emails.jobseekers.map((e) => (
                    <a
                      key={e}
                      href={`mailto:${e}`}
                      className="block text-[15px] font-bold text-green-primary transition-colors hover:text-gold-primary"
                    >
                      {e}
                    </a>
                  ))}
                </div>
                <div className="border-t border-gold-soft/50 pt-2.5">
                  <span className="block text-[10px] font-extrabold uppercase tracking-widest text-gold-primary/90 mb-1">
                    For Business & Employers
                  </span>
                  {CONTACT.emails.business.map((e) => (
                    <a
                      key={e}
                      href={`mailto:${e}`}
                      className="block text-[15px] font-bold text-green-primary transition-colors hover:text-gold-primary"
                    >
                      {e}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Head office */}
            <div className="rounded-3xl border border-gold-soft bg-white p-6 shadow-soft">
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-green-primary text-gold-primary">
                  <FaLocationDot />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-primary">
                  Office Location
                </span>
              </div>
              <p className="text-sm font-bold text-green-primary">{CONTACT.office.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-green-primary/80 font-medium">
                {CONTACT.office.address}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-gold-soft/50 pt-3">
                {CONTACT.office.googleBusinessProfile && (
                  <a
                    href={CONTACT.office.googleBusinessProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-bold text-green-primary hover:text-gold-primary transition-colors"
                  >
                    <FaLocationDot className="text-sm text-gold-primary" /> Google Business Profile
                  </a>
                )}
                <a
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-green-primary hover:text-gold-primary transition-colors"
                >
                  <FaLinkedinIn className="text-sm text-gold-primary" /> Connect on LinkedIn
                </a>
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-green-primary hover:text-gold-primary transition-colors"
                >
                  <FaInstagram className="text-sm text-gold-primary" /> Follow on Instagram
                </a>
              </div>
            </div>
          </Reveal>

          {/* Form column */}
          <Reveal dir="right" delay={0.1} className="lg:col-span-4">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              aria-label="Book a recruitment consultation with PLACE8"
              className="h-full rounded-3xl border border-gold-soft bg-white p-8 shadow-soft flex flex-col justify-between"
            >
              <div>
                <h3 className="font-display text-2xl font-light text-green-primary">
                  Hire Talent / Request Proposal
                </h3>
                <p className="mt-2 text-xs text-green-primary/80 font-medium">
                  Fill in your details — our recruitment leads respond promptly.
                </p>

                <div className="mt-6 space-y-4">
                  <Field
                    label="Your Name / Organization"
                    placeholder="Jane Doe (Acme Corp)"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                  />

                  <Field
                    label="Email or Phone Number"
                    placeholder="jane@company.com / +91 9876543210"
                    value={form.contactInfo}
                    onChange={(e) => setForm({ ...form, contactInfo: e.target.value })}
                    data-testid="contact-info"
                  />

                  <label className="block">
                    <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-green-primary">
                      Recruitment Service Required
                    </span>
                    <select
                      value={form.service}
                      onChange={(e) => setForm({ ...form, service: e.target.value })}
                      data-testid="contact-service"
                      name="service"
                      aria-label="Recruitment Service Required"
                      className="w-full rounded-2xl border border-gold-soft bg-gold-light px-5 py-4 text-sm text-green-primary outline-none focus:border-gold-primary focus:ring-2 focus:ring-gold-primary/30"
                    >
                      <option>Executive Search</option>
                      <option>Permanent Hiring</option>
                      <option>Bulk Hiring</option>
                      <option>IT Recruitment</option>
                      <option>Non-IT Recruitment</option>
                      <option>Healthcare Recruitment</option>
                      <option>BFSI Recruitment</option>
                      <option>Recruitment Process Outsourcing (RPO)</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-green-primary">
                      Hiring Message / Position Details
                    </span>
                    <textarea
                      rows={3}
                      placeholder="Tell us about the roles, number of positions, or domain requirements..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="contact-message"
                      name="message"
                      aria-label="Your hiring details"
                      className="w-full resize-none rounded-2xl border border-gold-soft bg-gold-light px-5 py-4 text-sm text-green-primary outline-none placeholder:text-green-primary/60 focus:border-gold-primary focus:ring-2 focus:ring-gold-primary/30"
                    />
                  </label>
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                className="btn-gold group mt-6 flex w-full items-center justify-center gap-3 rounded-full px-7 py-4 text-xs md:text-sm font-bold uppercase tracking-[0.1em] text-white transition-transform duration-300 hover:-translate-y-0.5"
              >
                <FaWhatsapp className="text-base" />
                Submit via WhatsApp
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>

          {/* Map column */}
          <Reveal dir="right" delay={0.2} className="lg:col-span-4">
            <div className="h-full min-h-[420px] overflow-hidden rounded-3xl border border-gold-soft shadow-soft relative flex flex-col bg-white">
              <div className="bg-green-primary px-5 py-3 text-white text-xs font-semibold flex items-center justify-between border-b border-gold-soft">
                <span>Location: <strong className="text-gold-primary">PLACE8 Sanpada, Navi Mumbai</strong></span>
                {CONTACT.office.googleBusinessProfile && (
                  <a
                    href={CONTACT.office.googleBusinessProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-primary hover:underline font-bold text-[11px] flex items-center gap-1"
                  >
                    View Profile →
                  </a>
                )}
              </div>
              <iframe
                title="PLACE8 Recruitment Consultancy Location"
                data-testid="google-map"
                src={CONTACT.office.mapSrc}
                width="100%"
                height="100%"
                className="flex-1 min-h-[380px]"
                style={{ border: 0, filter: "grayscale(0.1) contrast(1.05)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
